import React, { useCallback, useState } from "react";
import { Spinner, Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from "@heroui/react";
import { Typography, UniversalTable } from "../../../../components";
import { useTranslations } from "../../../../contexts/translation.context";
import { OnlyFansAccount } from "../../../../api/staff";
import {
  IdCell,
  AccountCell,
  CreatorCell,
  CampaignsCell,
  OrdersCell,
  StatusCell
} from "./index";
import { EmptyContentIc } from "../../../../components/icons";

interface OnlyFansAccountsTableProps {
  accounts: OnlyFansAccount[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onActivate?: (accountId: string) => void;
  onDeactivate?: (accountId: string) => void;
  onVerify?: (accountId: string) => void;
  onAddHeaders?: (accountId: string, accountName: string, ofUserId: string) => void;
}

/**
 * OnlyFansAccountsTable Component
 *
 * Main table component for displaying OnlyFans accounts.
 * Uses UniversalTable from the component library and custom cell renderers.
 *
 * Architecture:
 * - Follows the pattern from onlyu_front/partner/subscribers/orders
 * - Uses custom cell components for each column
 * - Integrates with HeroUI table components
 */
const OnlyFansAccountsTable: React.FC<OnlyFansAccountsTableProps> = ({
  accounts,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onActivate,
  onDeactivate,
  onVerify,
  onAddHeaders,
}) => {
  const { t } = useTranslations();

  // Define table columns
  const columns = [
    {
      key: "id",
      name: "ID",
      sortable: false
    },
    {
      key: "account",
      name: t('admin.onlyfans.columns.account'),
      sortable: false
    },
    {
      key: "creator",
      name: t('admin.onlyfans.columns.creator'),
      sortable: false
    },
    {
      key: "campaigns",
      name: t('admin.onlyfans.columns.campaigns'),
      sortable: false
    },
    {
      key: "orders",
      name: t('admin.onlyfans.columns.orders'),
      sortable: false
    },
    {
      key: "status",
      name: t('admin.onlyfans.columns.status'),
      sortable: false
    },
    {
      key: "actions",
      name: "",
      sortable: false
    }
  ];

  // Cell renderer
  const renderCell = useCallback((account: OnlyFansAccount, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return <IdCell account={account} />;
      case "account":
        return <AccountCell account={account} />;
      case "creator":
        return <CreatorCell account={account} />;
      case "campaigns":
        return <CampaignsCell account={account} />;
      case "orders":
        return <OrdersCell account={account} />;
      case "status":
        return <StatusCell account={account} />;
      case "actions":
        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                size="sm"
                variant="light"
                className="min-w-[32px] w-[32px] h-[32px] p-0"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 5.83333C10.9205 5.83333 11.6667 5.08714 11.6667 4.16667C11.6667 3.24619 10.9205 2.5 10 2.5C9.07953 2.5 8.33333 3.24619 8.33333 4.16667C8.33333 5.08714 9.07953 5.83333 10 5.83333Z" fill="#71717A"/>
                  <path d="M10 11.6667C10.9205 11.6667 11.6667 10.9205 11.6667 10C11.6667 9.07953 10.9205 8.33333 10 8.33333C9.07953 8.33333 8.33333 9.07953 8.33333 10C8.33333 10.9205 9.07953 11.6667 10 11.6667Z" fill="#71717A"/>
                  <path d="M10 17.5C10.9205 17.5 11.6667 16.7538 11.6667 15.8333C11.6667 14.9129 10.9205 14.1667 10 14.1667C9.07953 14.1667 8.33333 14.9129 8.33333 15.8333C8.33333 16.7538 9.07953 17.5 10 17.5Z" fill="#71717A"/>
                </svg>
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Account actions">
              {account.status === 'moderation' && (
                <DropdownItem
                  key="verify"
                  className="text-success"
                  color="success"
                  onPress={() => onVerify?.(account._id)}
                >
                  {t('admin.onlyfans.actions.verify')}
                </DropdownItem>
              )}
              <DropdownItem
                key="addHeaders"
                onPress={() => onAddHeaders?.(account._id, account.name, account.of_user_id)}
              >
                {t('admin.onlyfans.actions.addHeaders')}
              </DropdownItem>
              {account.status === 'deactivated' ? (
                <DropdownItem
                  key="activate"
                  className="text-success"
                  color="success"
                  onPress={() => onActivate?.(account._id)}
                >
                  {t('admin.onlyfans.actions.activate') || 'Активировать'}
                </DropdownItem>
              ) : (
                <DropdownItem
                  key="deactivate"
                  className="text-danger"
                  color="danger"
                  onPress={() => onDeactivate?.(account._id)}
                >
                  {t('admin.onlyfans.actions.deactivate')}
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        );
      default:
        return null;
    }
  }, [onActivate, onDeactivate, onVerify, onAddHeaders, t]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Empty state
  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc/>
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {t('admin.onlyfans.empty.title')}
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {t('admin.onlyfans.empty.description')}
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <UniversalTable
      data={accounts}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default OnlyFansAccountsTable;
